import React from 'react';
import { styled } from '@mui/material/styles';
import Typography, { TypographyProps } from '@mui/material/Typography';

interface TextEllipsisProps extends Omit<TypographyProps, 'title'> {
  /** e.g. 200 or "50%" */
  maxWidth?: number | string;
  /** number of lines to show before clamping; if omitted, single-line ellipsis is used */
  rows?: number;
}

const TextEllipsis: React.FC<TextEllipsisProps> = ({ children, maxWidth, rows, ...typographyProps }) => {
  const title = typeof children === 'string' ? children : undefined;

  return (
    <StyledEllipsis maxWidth={maxWidth} rows={rows} title={title} {...typographyProps}>
      {children}
    </StyledEllipsis>
  );
};

export default TextEllipsis;

const StyledEllipsis = styled(Typography, { shouldForwardProp: prop => prop !== 'maxWidth' && prop !== 'rows' })<
  Pick<TextEllipsisProps, 'maxWidth' | 'rows'>
>(({ theme, maxWidth, rows }) => {
  const lineHeightValue = theme.typography.body2.lineHeight;
  const lineHeightCss = typeof lineHeightValue === 'number' ? `${lineHeightValue}em` : lineHeightValue;

  return {
    ...(maxWidth != null && {
      maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
    }),
    ...(rows != null
      ? {
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: rows,
          overflow: 'hidden',
          lineHeight: lineHeightCss,
        }
      : {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: lineHeightCss,
        }),
  };
});
